import type { Media, MediaKind } from '@prisma/client'
import { UploadIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import * as EmptyState from '~/components/EmptyState'
import { formatFileSize, getAcceptString } from '~/helpers/media'
import type { FileUploadProgress } from '~/hooks/use-file-upload'
import { Button, Alert } from '~/ui'
import { cn } from '~/utils/classnames'
import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from '~/utils/constants'

import { MediaItem } from './MediaItem'

interface MediaGridProps {
  media: Media[]
  selectedMediaIds: Set<string>
  uploadInputId: string
  isUploading: boolean
  uploadProgress: FileUploadProgress[]
  uploadError: string | null
  onSelect: (mediaId: string, media: Media, selected: boolean) => void
  onDelete: (mediaId: string) => void
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
  onCancelUpload: (fileId: string) => void
  getProgressForMedia: (media: Media) => FileUploadProgress | undefined
  allowedMediaKinds?: MediaKind[]
}

export function MediaGrid({
  media,
  selectedMediaIds,
  uploadInputId,
  isUploading,
  uploadProgress,
  uploadError,
  onSelect,
  onDelete,
  onFileUpload,
  onCancelUpload,
  getProgressForMedia,
  allowedMediaKinds,
}: MediaGridProps) {
  const { t } = useTranslation()

  // Determine accept string based on allowed media kinds
  const acceptString =
    allowedMediaKinds?.length === 1 && allowedMediaKinds[0] === 'image'
      ? getAcceptString('image')
      : allowedMediaKinds?.length === 1 && allowedMediaKinds[0] === 'video'
        ? getAcceptString('video')
        : getAcceptString('all')

  const unselectedMedia = media.filter((m) => !selectedMediaIds.has(m.id))

  return (
    <div className="flex-1 p-4">
      {/* Header */}
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-sm font-medium max-sm:hidden">
          {selectedMediaIds.size > 0
            ? t('components.media_manager.media_assets_selected', {
                nSelected: selectedMediaIds.size,
              })
            : t('action.click_to_add')}
        </h2>

        <input
          hidden
          type="file"
          accept={acceptString}
          id={uploadInputId}
          onChange={onFileUpload}
          disabled={isUploading}
          aria-label={t('action.upload_media')}
          multiple
        />

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
          {uploadError && <Alert title={uploadError} type="error" />}
          <Button
            asChild
            variant="outline"
            isLoading={isUploading}
            className="cursor-pointer"
          >
            <label tabIndex={0} htmlFor={uploadInputId}>
              <UploadIcon />
              {t('action.upload_media')}
            </label>
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid w-full grid-cols-2 gap-4 pb-56 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {unselectedMedia.map((mediaItem) => {
          const isPreview = mediaItem.id.startsWith('temp-')
          const progress = getProgressForMedia(mediaItem)

          return (
            <MediaItem
              key={mediaItem.id}
              media={mediaItem}
              isSelected={selectedMediaIds.has(mediaItem.id)}
              isPreview={isPreview}
              progress={progress}
              onSelect={(selected) =>
                onSelect(mediaItem.id, mediaItem, selected)
              }
              onDelete={() => onDelete(mediaItem.id)}
              onCancelUpload={() => progress && onCancelUpload(progress.fileId)}
            />
          )
        })}
      </div>
    </div>
  )
}

interface MediaGridEmptyStateProps {
  uploadInputId: string
  isUploading: boolean
  uploadError: string | null
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
  allowedMediaKinds?: MediaKind[]
  className?: string
}

export function MediaGridEmptyState({
  uploadInputId,
  isUploading,
  uploadError,
  onFileUpload,
  allowedMediaKinds,
  className,
}: MediaGridEmptyStateProps) {
  const { t } = useTranslation()

  // Determine accept string based on allowed media kinds
  const acceptString =
    allowedMediaKinds?.length === 1 && allowedMediaKinds[0] === 'image'
      ? getAcceptString('image')
      : allowedMediaKinds?.length === 1 && allowedMediaKinds[0] === 'video'
        ? getAcceptString('video')
        : getAcceptString('all')

  return (
    <div
      className={cn(
        'relative flex w-full grow items-center justify-center',
        className
      )}
    >
      {/* Upload info UI/control */}
      <div className="z-10 max-w-[386px] text-center">
        <EmptyState.MediaManager className="mx-auto" />
        <div className="mx-auto mt-8 text-sm font-medium">
          <h2 className="text-foreground">
            {t('components.media_manager.empty_state.title')}
          </h2>
          <p className="text-foreground/64 mt-2">
            {t('components.media_manager.empty_state.subtitle', {
              imageSize: formatFileSize(MAX_IMAGE_FILE_SIZE),
              videoSize: formatFileSize(MAX_VIDEO_FILE_SIZE),
            })}
          </p>
          <div className="mt-5">
            <input
              hidden
              type="file"
              accept={acceptString}
              id={uploadInputId}
              name="media"
              onChange={onFileUpload}
              disabled={isUploading}
              aria-label={t('action.upload_media')}
              multiple
            />

            <Button
              asChild
              variant="outline"
              isLoading={isUploading}
              className="cursor-pointer"
            >
              <label tabIndex={0} htmlFor={uploadInputId}>
                <UploadIcon />
                {t('action.upload_media')}
              </label>
            </Button>
          </div>
        </div>
      </div>

      {/* Background tiles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="from-background to-background/8 absolute top-0 block h-1/2 w-full bg-linear-to-b from-40% to-100%" />
        <div className="grid grid-cols-2 gap-3 pb-56 sm:grid-cols-3 sm:gap-6 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => i + 1).map((columnNumber) => (
            <div className="flex flex-1 flex-col gap-6" key={columnNumber}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map((i) => (
                <div
                  className={cn(
                    'bg-under/2 flex h-56 rounded-md',
                    columnNumber % 2 === 1 && i === 1 && 'h-28'
                  )}
                  key={i}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
